// contexts/SubdomainContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../api/api';
import AuthContext from './AuthContext';

// Create the subdomain context
const SubdomainContext = createContext();

export const SubdomainProvider = ({ children }) => {
  const [subdomainCenter, setSubdomainCenter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subdomain, setSubdomain] = useState(null);
  const [hasFeature, setHasFeature] = useState(false);
  const { center: authCenter } = useContext(AuthContext);

  // Extract subdomain from URL
  useEffect(() => {
    const detectSubdomain = async () => {
      try {
        const hostname = window.location.hostname;
        
        // For test.memoraize.io:3000/login or similar development URLs
        // Extract the subdomain (test in this case)
        if (hostname.includes('memoraize.io') || 
            hostname.includes('.memoraize.') || 
            hostname === 'localhost' || 
            /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
          
          // First attempt: Check for subdomain in hostname
          let extractedSubdomain = null;
          
          // Check if using a test subdomain URL pattern like test.memoraize.io
          if (hostname.includes('.memoraize.')) {
            const parts = hostname.split('.');
            if (parts.length > 2) {
              extractedSubdomain = parts[0].toLowerCase();
              console.log('SubdomainContext: Extracted subdomain from hostname:', extractedSubdomain);
            }
          }
          
          // Second attempt: Check if subdomain is in query string (for development)
          if (!extractedSubdomain) {
            const urlParams = new URLSearchParams(window.location.search);
            extractedSubdomain = urlParams.get('subdomain');
            if (extractedSubdomain) {
              console.log('SubdomainContext: Using subdomain from query parameter:', extractedSubdomain);
            }
          }
          
          // Use test as fallback for development (hard-coded for Image #1 example)
          if (!extractedSubdomain && (hostname === 'localhost' || hostname.includes(':3000'))) {
            extractedSubdomain = 'test';
            console.log('SubdomainContext: Using default test subdomain for development');
          }
          
          if (extractedSubdomain) {
            setSubdomain(extractedSubdomain);
            await loadCenterBySubdomain(extractedSubdomain);
          } else {
            // No subdomain detected, fall back to auth center if available
            if (authCenter) {
              console.log('SubdomainContext: No subdomain detected, using auth center:', authCenter.name);
              setSubdomainCenter(authCenter);
              checkFeature(authCenter);
            } else {
              console.log('SubdomainContext: No subdomain or auth center available, showing default branding');
            }
            setLoading(false);
          }
          return;
        }
        
        // Standard production case: Extract subdomain from hostname
        const parts = hostname.split('.');
        if (parts.length > 2) {
          const extractedSubdomain = parts[0].toLowerCase();
          console.log('SubdomainContext: Extracted production subdomain:', extractedSubdomain);
          setSubdomain(extractedSubdomain);
          await loadCenterBySubdomain(extractedSubdomain);
        } else {
          // No subdomain in production, use auth center if available
          if (authCenter) {
            console.log('SubdomainContext: No production subdomain, using auth center:', authCenter.name);
            setSubdomainCenter(authCenter);
            checkFeature(authCenter);
          }
          setLoading(false);
        }
      } catch (err) {
        console.error('Error detecting subdomain:', err);
        // Still use auth center on error if available
        if (authCenter) {
          setSubdomainCenter(authCenter);
          checkFeature(authCenter);
        }
        setLoading(false);
      }
    };
    
    // Always run the detectSubdomain logic immediately, regardless of authCenter
    // This ensures the subdomain center is loaded as early as possible, even on login page
    console.log('SubdomainContext: Running subdomain detection');
    detectSubdomain();
    
  }, [authCenter]);
  
  // Load center data by subdomain
  const loadCenterBySubdomain = async (sub) => {
    try {
      // If the auth center has this subdomain, use it directly
      if (authCenter && authCenter.subdomainPrefix === sub) {
        setSubdomainCenter(authCenter);
        checkFeature(authCenter);
        setLoading(false);
        return;
      }
      
      // Otherwise, fetch from API
      const response = await api.getCenterBySubdomain(sub);
      
      if (response.data && response.data.success) {
        const centerData = response.data.data;
        setSubdomainCenter(centerData);
        checkFeature(centerData);
      } else {
        // Fallback to auth center if subdomain not found
        console.log('Subdomain not found, falling back to auth center');
        setSubdomainCenter(authCenter);
        checkFeature(authCenter);
      }
    } catch (error) {
      console.error('Error loading center by subdomain:', error);
      // Fallback to auth center on error
      setSubdomainCenter(authCenter);
      checkFeature(authCenter);
    } finally {
      setLoading(false);
    }
  };
  
  // Check if center has branding feature (for MVP, all centers do)
  const checkFeature = (center) => {
    // In the MVP, all centers have the branding feature
    // When proper licensing is implemented, this should check with licenseHelper
    setHasFeature(true); // Always enable this feature for all centers
  };
  
  // Context value
  const contextValue = {
    center: subdomainCenter,
    subdomain,
    loading,
    hasFeature,
    // Helper to check if a subdomain is active
    hasSubdomain: !!subdomain
  };
  
  return (
    <SubdomainContext.Provider value={contextValue}>
      {children}
    </SubdomainContext.Provider>
  );
};

// Custom hook to use the subdomain context
export const useSubdomain = () => useContext(SubdomainContext);

export default SubdomainContext;